import { type CreateUserPayload } from "../../../../../api/auth";
import { useContext, useState, type FormEvent } from "react";
import { ModalContext } from "../../../store/ModalContext";
import { UsersContext } from "../../../store/UsersContext";
import { useNavigate } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { AccountRole } from "../../../utils/types";

/** Submit with `<button type="submit" form={USER_EMPLOYEE_FORM_ID|USER_ADMIN_FORM_ID} />` outside this `<form>`. */
export const USER_EMPLOYEE_FORM_ID = "create-user-employee-form";
export const USER_ADMIN_FORM_ID = "create-user-admin-form";

type UserEmployeeFormProps = {
  role?: AccountRole.Admin | AccountRole.Employee;
  onSubmitChange: (isSubmitting: boolean) => void;
};

const employeeValidationMessage =
  "Please fill out first name, last name, email, and phone number!";

const UserEmployeeForm = ({
  role = AccountRole.Employee,
  onSubmitChange,
}: UserEmployeeFormProps) => {
  const { openModal } = useContext(ModalContext);
  const { createUser } = useContext(UsersContext);
  const { isAuthenticated } = useAuthUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const navigate = useNavigate();

  const formId =
    role === AccountRole.Admin ? USER_ADMIN_FORM_ID : USER_EMPLOYEE_FORM_ID;

  function staffFormIsValid(data: Record<string, FormDataEntryValue>): boolean {
    const first = String(data.firstName ?? "").trim();
    const last = String(data.lastName ?? "").trim();
    const email = String(data.email ?? "").trim();
    const phone = String(data.phone ?? "").trim();
    return Boolean(first && last && email && phone);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    const fd = new FormData(event.currentTarget);
    const data = Object.fromEntries(fd.entries());

    if (!staffFormIsValid(data)) {
      setFormError(employeeValidationMessage);
      return;
    }

    setFormError(null);

    if (!isAuthenticated) {
      openModal({
        body: "You must be logged in (MongoDB) to create users.",
        showCloseButton: true,
      });
      return;
    }

    setIsSubmitting(true);
    onSubmitChange(true);

    try {
      await createUser({
        role: role,
        firstName: data.firstName as string,
        lastName: data.lastName as string,
        email: data.email as string,
        phoneNumber: data.phone as string,
      } as CreateUserPayload);
      openModal({
        title: "Success!",
        body: "The account has been created and an email has been sent.",
        showCloseButton: true,
      });
      navigate("/dashboard/admin/manage-users");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to create user";
      openModal({ body: msg, showCloseButton: true });
    } finally {
      setIsSubmitting(false);
      onSubmitChange(false);
    }
  }

  return (
    <form
      id={formId}
      noValidate
      onSubmit={handleSubmit}
      onInput={() => setFormError(null)}
      className="form"
    >
      <div className="form-fields-container">
        <div className="modal-form-input-pair">
          <label htmlFor="first-name" className="sr-only">
            First Name
          </label>
          <input
            type="text"
            name="firstName"
            id="first-name"
            placeholder="First Name"
          />
          <label htmlFor="last-name" className="sr-only">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            id="last-name"
            placeholder="Last Name"
          />
        </div>
        <div className="modal-form-input-pair">
          <label htmlFor="email" className="sr-only">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            placeholder="Email"
          />
          <label htmlFor="phone" className="sr-only">
            Phone Number
          </label>
          <input
            type="phone"
            name="phone"
            id="phone"
            placeholder="Phone"
          />
        </div>
      </div>
      {formError && (
        <p className="pickup-form-validation-msg" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
};

export default UserEmployeeForm;
